/**
 * Number and bin-label formatting for the `density-histogram` plot (plots plan M).
 *
 * Python owns every figure: bin edges, shares and statistics arrive rounded,
 * and each bin carries its `labelForm` (and `labelMax` for whole-number bins).
 * This module only prints them: numbers through `Intl` in the payload's
 * locale, bin names through the `plot_bin_*` templates of `deck_labels.py`
 * (`a to b`, `a to <b`, `a+`, never a bare hyphen). A template the payload
 * does not carry prints nothing: the plot never falls back to English.
 *
 * Plot code never imports the frontend's `@/lib`.
 */

import { PlotDataOf, PlotLabelsOf } from '../contract'

export type DensityHistogramData = PlotDataOf<'density-histogram'>
export type DensityHistogramMeasure = DensityHistogramData['measure']
export type DensityHistogramSeriesData = DensityHistogramData['series'][number]
export type DensityHistogramBinData = DensityHistogramSeriesData['bins'][number]
export type DensityHistogramPlotLabels = Partial<PlotLabelsOf<'density-histogram'>>

/** How one surface prints a histogram's numbers and bin names. */
export interface DensityFormatter {
  /** A statistic (mean, band edge) at the measure's printed decimals. */
  value(value: number | null | undefined): string
  /** The median, wherever it prints (header, tile). */
  median(value: number | null | undefined): string
  /** A figure printed on the plot itself (the median note). */
  annotation(value: number): string
  /** The name under one bar, or null when the surface has no template for it. */
  binLabel(bin: DensityHistogramBinData, index: number): string | null
  /** A closed range (the middle-50% band), or null without a template. */
  range(low: number, high: number): string | null
}

/** Fills `{name}` placeholders; an unknown placeholder is left as written. */
export function fillDensityTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match))
}

/** Edges are Python's nice numbers (2.5, 0.25): printed exactly, never re-rounded. */
const EDGE_MAX_DECIMALS = 6

/** The producer's rounding rule (half away from zero) for printed labels; `roundingMode` is ES2023. */
function withHalfEven(options: Intl.NumberFormatOptions): Intl.NumberFormatOptions {
  return { ...options, roundingMode: 'halfExpand' } as Intl.NumberFormatOptions
}

function numberText(value: number, locale: string, maxDecimals: number, minDecimals = 0): string {
  try {
    return new Intl.NumberFormat(locale, withHalfEven({ minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals })).format(value)
  } catch {
    return String(value)
  }
}

function currencyText(value: number, locale: string, code: string, maxDecimals: number, minDecimals: number): string {
  try {
    return new Intl.NumberFormat(
      locale,
      withHalfEven({ style: 'currency', currency: code, minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals }),
    ).format(value)
  } catch {
    // A code Intl cannot format is still the stored code.
    return `${code} ${numberText(value, locale, maxDecimals, minDecimals)}`
  }
}

/**
 * `statistic`: a statistic's label, not a bin edge. A sum of money prints a
 * statistic at its stamped places, trailing zeros kept ("$43.50": Python
 * stamps a currency's minor unit, the spelling every other surface prints);
 * an edge, and any other measure, drops them.
 */
function measureText(
  value: number,
  measure: DensityHistogramMeasure,
  locale: string,
  maxDecimals: number,
  statistic = false,
): string {
  const code = typeof measure.currencyCode === 'string' ? measure.currencyCode.trim() : ''
  const minDecimals = statistic && measure.valueFormat === 'currency' ? maxDecimals : 0
  if (measure.valueFormat === 'currency' && code) {
    return currencyText(value, locale, code, maxDecimals, minDecimals)
  }
  const text = numberText(value, locale, maxDecimals, minDecimals)
  if (measure.valueFormat === 'percent') {
    return `${text}%`
  }
  if (measure.valueFormat === 'duration' && measure.durationUnit) {
    return `${text} ${measure.durationUnit}`
  }
  return text
}

/**
 * The decimals one statistic's label prints at. Python stamps statistics at
 * `decimals + 2` so marks sit at the value; the label rounds to the measure's
 * `decimals`, except a value exactly half way between two printed steps keeps
 * that half. The formatter applies this only to the median of whole answers
 * (`isIntegerMeasure`): an even-n median, 14.5, stays 14.5, as the deck always
 * printed it. Presentation only: the figure itself is never changed.
 */
export function densityLabelDecimals(value: number, decimals: number): number {
  const scaled = value * Math.pow(10, decimals)
  const isWhole = Math.abs(scaled - Math.round(scaled)) < 1e-9
  const isHalf = Math.abs(Math.abs(scaled - Math.trunc(scaled)) - 0.5) < 1e-9
  return !isWhole && isHalf ? decimals + 1 : decimals
}

/**
 * The formatter for a Python-built payload: `locale` is the deck language on
 * the deck and the viewer's UI language elsewhere.
 */
export function densityFormatter(
  measure: DensityHistogramMeasure,
  labels: DensityHistogramPlotLabels,
  locale: string,
): DensityFormatter {
  const statDecimals = Math.max(0, Math.min(3, Math.round(measure.decimals)))
  const edge = (value: number) => measureText(value, measure, locale, EDGE_MAX_DECIMALS)
  const medianText = (value: number) =>
    measureText(value, measure, locale, measure.isIntegerMeasure ? densityLabelDecimals(value, statDecimals) : statDecimals, true)

  return {
    value(value) {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return ''
      }
      return measureText(value, measure, locale, statDecimals, true)
    },
    median(value) {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return ''
      }
      return medianText(value)
    },
    annotation(value) {
      return medianText(value)
    },
    binLabel(bin) {
      if (bin.labelForm === 'point') {
        return edge(bin.min)
      }
      if (bin.labelForm === 'openTop') {
        return labels.plot_bin_open_top ? fillDensityTemplate(labels.plot_bin_open_top, { low: edge(bin.min) }) : null
      }
      if (bin.labelForm === 'openBottom') {
        return labels.plot_bin_open_bottom ? fillDensityTemplate(labels.plot_bin_open_bottom, { high: edge(bin.max) }) : null
      }
      if (bin.labelForm === 'halfOpen') {
        return labels.plot_bin_half_open
          ? fillDensityTemplate(labels.plot_bin_half_open, { low: edge(bin.min), high: edge(bin.max) })
          : null
      }
      const high = typeof bin.labelMax === 'number' ? bin.labelMax : bin.max
      return labels.plot_bin_range ? fillDensityTemplate(labels.plot_bin_range, { low: edge(bin.min), high: edge(high) }) : null
    },
    range(low, high) {
      return labels.plot_bin_range
        ? fillDensityTemplate(labels.plot_bin_range, {
            low: measureText(low, measure, locale, statDecimals, true),
            high: measureText(high, measure, locale, statDecimals, true),
          })
        : null
    },
  }
}

/** A share Python rounded (one decimal), printed in the locale. */
export function formatDensityShare(share: number, locale: string): string {
  return `${numberText(share, locale, 1)}%`
}
